import React, {
  useCallback,
  useEffect,
  useReducer,
  useState
} from 'react';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Typography
} from 'antd';
import '../../../components/Common/Form.css';
import { getTrainers } from '../../../services/trainerServices';
import ResourcesSection from './ResourcesSection';

const { Text } = Typography;
const { Option } = Select;

const CommunityEventForm = ({
  initialValues,
  onSubmit,
  status,
  setStatus,
  type,
  hostIsTrainer,
  setHostIsTrainer,
  setTrainer,
  communityList
}) => {
  const [form] = Form.useForm();
  const dateFormat = 'Do MMMM YYYY h:mm:ssA Z';
  const [fetchedTrainers, setFetchedTrainers] = useState([]);
  const [fetchingTrainers, setFetchingTrainers] = useState(false);
  const fetchTrainersHandler = useCallback(
    (trainerSearch) => {
      setFetchingTrainers(true);
      getTrainers(1, 1000, trainerSearch)
        .then((response) => {
          const data = response.data.paginatedList.map((trainer) => ({
            label: `${trainer.firstName}: ${trainer.lastName}`,
            value: trainer._id,
            ...trainer
          }));
          setFetchedTrainers(data);
          setFetchingTrainers(false);
        })
        .catch((err) => {
          setStatus({
            error: true,
            message: `${err}`
          });
        });
    },
    [setStatus]
  );

  useEffect(() => fetchTrainersHandler(), [fetchTrainersHandler]);

  return (
    <Form
      labelCol={{ span: 7 }}
      wrapperCol={{ span: 21 }}
      onFinish={onSubmit}
      initialValues={initialValues}
      form={form}>
      <section>
        <Form.Item label="Communities" name="communities" required={false}>
          {type.toLowerCase().includes('add') ? (
            <Select
              mode={'multiple'}
              placeholder="Select community(s)"
              style={{ width: '100%' }}>
              {communityList?.map((option, index) => (
                <Option key={index} value={option?._id}>
                  {option?.title}
                </Option>
              ))}
            </Select>
          ) : (
            <Text>
              {initialValues.code}: {initialValues.title}
            </Text>
          )}
        </Form.Item>
        <Form.Item
          label="Type*"
          name="type"
          rules={[
            {
              required: true,
              message: 'type is required'
            }
          ]}>
          <Select>
            <Select.Option value="live">Live</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Title*"
          name="title"
          rules={[{ required: true, message: 'Please input title!' }]}>
          <Input />
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: false }]}>
          <Input />
        </Form.Item>
        <section>
          <Text strong="true">Card Image</Text>
          <Form.Item
            label="Mobile Image Source"
            name="mobileImgSrc"
            rules={[{ required: false }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="Desktop Image Source"
            name="desktopImgSrc"
            rules={[{ required: false }]}>
            <Input />
          </Form.Item>
        </section>
        <section>
          <Text strong="true">Host*</Text>
          <Form.Item required={false} valuePropName="checked">
            <Checkbox
              defaultChecked={false}
              onChange={(e) => {
                setHostIsTrainer(e.target.checked);
                if (!e.target.checked) setTrainer(null);
              }}
            />
            Host Is Trainer?
          </Form.Item>
          {hostIsTrainer ? (
            <Form.Item label="Trainer" name="trainer" required={false}>
              <Select
                showSearch
                loading={fetchingTrainers}
                placeholder="Select trainers"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.[1]
                    ?.toLowerCase?.()
                    ?.includes?.(input?.toLowerCase())
                }
                onSelect={(e) => {
                  setTrainer(
                    fetchedTrainers.find((trainer) => trainer.value === e)
                  );
                }}
                style={{ width: '100%' }}>
                {fetchedTrainers?.map((option, index) => (
                  <Option key={index} value={option?.value}>
                    <img
                      alt="profileImg"
                      width={30}
                      height={30}
                      style={{ borderRadius: '100%', marginRight: '16px' }}
                      src={option?.profileImage}
                    />
                    {option?.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <>
              <Form.Item
                label="First Name"
                name="hostFirstName"
                rules={[
                  {
                    required: true,
                    message: 'Please input host first name!'
                  }
                ]}>
                <Input />
              </Form.Item>
              <Form.Item
                label="Last Name"
                name="hostLastName"
                rules={[
                  {
                    required: true,
                    message: 'Please input host last name!'
                  }
                ]}>
                <Input />
              </Form.Item>
              <Form.Item
                label="Profile Image"
                name="hostProfileImage"
                rules={[
                  {
                    required: true,
                    message: 'Please input host profile image!'
                  }
                ]}>
                <Input />
              </Form.Item>
            </>
          )}
        </section>
        <Form.Item
          label="Start Time (Local)"
          name="startTime"
          rules={[
            { required: true, message: 'Please input Start Time!' }
          ]}>
          <DatePicker
            showTime={true}
            showNow={false}
            format={dateFormat}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label="End Time (Local)"
          name="endTime"
          rules={[{ required: true, message: 'Please input End Time!' }]}>
          <DatePicker
            showTime={true}
            showNow={false}
            format={dateFormat}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </section>

      <Divider />
      <Text strong="true">Details</Text>
      <section>
        <Form.Item label="Live link" name="liveLink">
          <Input />
        </Form.Item>
        <Form.Item label="Recording link" name="recordingLink">
          <Input />
        </Form.Item>
        <Form.Item
          label="Status"
          name="status"
          rules={[
            {
              required: true,
              message: 'Please input event status!'
            }
          ]}>
          <Select>
            <Select.Option value="Active">Active</Select.Option>
            <Select.Option value="Cancelled">Cancelled</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          label="Is Active?"
          name="isActive"
          rules={[
            {
              required: true,
              message: 'Required field!'
            }
          ]}>
          <Select>
            <Select.Option value="True">True</Select.Option>
            <Select.Option value="False">False</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          label="Live Location Label"
          name={['liveLocation', 'label']}>
          <Input />
        </Form.Item>
        <Form.Item
          label="Live Location Link"
          name={['liveLocation', 'link']}>
          <Input />
        </Form.Item>
      </section>
      <Divider />
      <Text strong="true">Resources</Text>
      <ResourcesSection form={form}></ResourcesSection>
      <Divider />
      {status.type === 'ERROR' ? (
        <Text type="danger">{status.message}</Text>
      ) : null}
      {status.type === 'SUCCESS' ? (
        <Text type="success">Successfully Updated!</Text>
      ) : null}
      {type !== 'Clone' ? (
        <Row justify="end" gutter={12}>
          <Col>
            <Button type="primary" htmlType="submit">
              {type}
            </Button>
          </Col>
        </Row>
      ) : (
        <Row justify="end" gutter={12}>
          <Col>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default CommunityEventForm;
